/**
 * Wave 3 v1.1 — "Referred by ___" social-proof badge.
 *
 * Reads the `qt_referral_code` cookie (set earlier by
 * <ReferralAttribution> when the visitor landed with `?ref=`), looks up
 * the referrer's display name via /api/referral/lookup, and renders a
 * pill above the signup form:
 *
 *   ✦ Referred by Jane
 *     You and they both earn rewards when you upgrade.
 *
 * Renders nothing when:
 *   • There's no `qt_referral_code` cookie
 *   • The code is invalid / unknown
 *   • The lookup call fails
 *
 * Mount above any signup CTA. Cheap — one GET on mount, no polling.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const COOKIE_NAME = "qt_referral_code";


function _readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|;\\s*)${name}=([^;]+)`),
  );
  return match ? decodeURIComponent(match[1]) : null;
}


export default function ReferredByBadge({ className = "" }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    const code = _readCookie(COOKIE_NAME);
    if (!code) return;

    let cancelled = false;
    axios.get(`${API}/api/referral/lookup`, {
      params: { code },
      withCredentials: false,
    })
      .then((r) => {
        if (!cancelled && r.data?.valid) {
          setData(r.data);
        }
      })
      .catch(() => { /* silent — badge stays hidden on lookup failure */ });

    return () => { cancelled = true; };
  }, []);

  if (!data) return null;

  return (
    <div
      data-testid="referred-by-badge"
      className={`flex items-start gap-2 rounded-md px-3 py-2 ${className}`}
      style={{
        border: "1px solid #9B7CFF",
        background: "rgba(155,124,255,0.10)",
      }}
    >
      <Sparkles size={14} style={{ color: "#9B7CFF", flexShrink: 0, marginTop: 1 }} />
      <div>
        <div className="qt-mono text-xs font-semibold"
             style={{ color: "#9B7CFF", letterSpacing: "0.10em" }}
             data-testid="referred-by-badge-text">
          REFERRED BY {(data.referrer_name || data.code).toUpperCase()}
        </div>
        <div className="text-xs mt-0.5" style={{ color: "var(--qt-text-2)" }}>
          Both of you earn rewards when you upgrade to a paid plan.
        </div>
      </div>
    </div>
  );
}
